import React from 'react';
import { adminShortfallReason, isAdminWalletShort } from '../../../ClientView/walletShortfall';
import { AUTO_CHARGE_UI_ENABLED } from '../../../ClientView/ClientWallet/autoPay';
import './paymentModeSelector.css';

const money = (n) =>
  `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin checkout selector (payments restructure Phase 3). Three ways to settle a
 * booking now: charge the client's saved card, charge the wallet, or invoice it
 * later. `context` comes from GET /api/bookings/admin/payment-context/:clientId:
 *   { card_charge_enabled, active_card: { masked_pan, scheme, expiry } | null,
 *     wallet: { available_balance, total_balance } }
 * `total` is the live booking total (incl. VAT). The parent owns `mode` and the
 * pre-selection; this component only renders + reports clicks.
 */
export default function PaymentModeSelector({ mode, onChange, context, total }) {
  if (!context) return null;

  const available = Number(context.wallet?.available_balance || 0);
  const cardAvailable = !!context.card_charge_enabled && !!context.active_card;
  // ZERO FLOOR (P4, owner demand #1). Charging a wallet that doesn't cover the
  // booking would drive AVAILABLE below zero — which admins must not be able to
  // do either. Previously this option was rendered enabled and merely warned
  // that the booking "will be Pending", so staff learned the rule by clicking
  // and failing. It is now DISABLED with the shortfall stated up front, and the
  // escape hatches (saved card, invoice later, client top-up) are named in the
  // subtitle so the refusal comes with somewhere to go.
  const walletShort = total > 0 && isAdminWalletShort({ cost: total, available });
  const shortReason = walletShort ? adminShortfallReason({ cost: total, available }) : null;
  const card = context.active_card;

  const opt = (value, disabled, title, subtitle, reason = null) => {
    const active = mode === value;
    return (
      <button
        type="button"
        key={value}
        className={`pms__opt${active ? ' pms__opt--active' : ''}${disabled ? ' pms__opt--disabled' : ''}`}
        onClick={() => { if (!disabled) onChange(value); }}
        disabled={disabled}
        aria-pressed={active}
        title={disabled && reason ? reason : undefined}
      >
        <span className="pms__optTitle">{title}</span>
        <span className="pms__optSub">{subtitle}</span>
      </button>
    );
  };

  return (
    <div className="pms">
      <div className="pms__label">How is this booking paid?</div>
      <div className="pms__row">
        {/* Saved-card (merchant-initiated) charging is hidden while
            AUTO_CHARGE_UI_ENABLED is off — the same master switch that hides
            every auto-pay surface on the client side until MIT lands (owner
            2026-07-24: "why saved card still available … it really shouldn't
            be"). Admins settle with wallet or invoice-later only for now. */}
        {AUTO_CHARGE_UI_ENABLED && opt(
          'card',
          !cardAvailable,
          'Saved card',
          cardAvailable
            ? `${card.scheme ? card.scheme.toUpperCase() : 'Card'} ${card.masked_pan || ''} — charged now`
            : (!context.card_charge_enabled ? 'Card charging unavailable' : 'No saved card on file'),
        )}
        {opt(
          'wallet',
          walletShort,
          context.wallet?.wallet_label ? `${context.wallet.wallet_label} wallet` : 'Wallet',
          walletShort
            ? `${money(available)} available — ${money(total - available)} short`
            : `${money(available)} available`,
          shortReason,
        )}
        {opt(
          'invoice_later',
          false,
          'Invoice later',
          'No payment taken now',
        )}
      </div>
      {/* The option is disabled, but `mode` can still BE 'wallet' — the admin may
          have picked it while the total was lower and then raised the price. The
          shortfall is therefore stated as a hard block here too, and AddBooking /
          EditBooking disable Submit on the same condition. Never fail on click. */}
      {walletShort && (
        <div className="pms__floor">
          <div className="pms__floorTitle">
            The wallet can’t cover this booking — {money(total - available)} short
          </div>
          <div className="pms__floorBody">
            Charging it would take the client’s available balance below zero, which isn’t
            allowed for staff or clients. {AUTO_CHARGE_UI_ENABLED ? 'Charge the saved card, invoice it later,' : 'Invoice it later'} or ask
            the client to top up {money(total - available)} first.
          </div>
        </div>
      )}

      {mode === 'wallet' && !walletShort && (
        <div className="pms__wallet">
          <div className="pms__walletRow">
            <span>{context.wallet?.wallet_label ? `${context.wallet.wallet_label} wallet — available` : 'Available balance'}</span>
            <span className="pms__walletVal">{money(available)}</span>
          </div>
          <div className="pms__walletRow">
            <span>This booking</span>
            <span className="pms__walletVal pms__walletVal--neg">−{money(total)}</span>
          </div>
          <div className="pms__walletRow pms__walletRow--after">
            <span>Balance after</span>
            <span className={`pms__walletVal${available - total < -0.005 ? ' pms__walletVal--neg' : ''}`}>
              {money(available - total)}
            </span>
          </div>
        </div>
      )}
      {mode === 'card' && cardAvailable && (
        <div className="pms__hint">
          The client’s saved card is charged immediately and a receipt is recorded.
        </div>
      )}
      {mode === 'invoice_later' && (
        <div className="pms__hint">
          No payment is taken — the booking is billed on the client’s invoice later.
        </div>
      )}
    </div>
  );
}
